import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Home as HomeIcon, Heart, HandHelping, AlertTriangle, MapPin, Building2 } from "lucide-react";
import { http } from "@/lib/api";
import { APPEALS } from "@/data/appeals";
import { resolveLatLng } from "@/data/cityCoords";
import { useGeo } from "@/lib/useGeo";

// Layer definitions. Each layer contributes coloured pins to the map.
const LAYERS = [
  { key: "orgs", label: "Organizations", color: "#3B5AA6", icon: Building2 },
  { key: "housing", label: "Housing Projects", color: "#2F8F5C", icon: HomeIcon },
  { key: "opportunities", label: "Volunteer Opportunities", color: "#D97442", icon: HandHelping },
  { key: "appeals", label: "Emergency Appeals", color: "#B23A3A", icon: AlertTriangle },
  { key: "stories", label: "Joy Stories", color: "#5A3C89", icon: Heart },
];

// Build a coloured circular div-icon (avoids bundling default Leaflet marker PNGs).
const buildIcon = (color) =>
  L.divIcon({
    className: "wofy-pin",
    html: `<span style="
      display:block; width:22px; height:22px; border-radius:50%;
      background:${color}; border:3px solid #FDFBF7;
      box-shadow: 0 2px 6px rgba(0,0,0,0.25);
    "></span>`,
    iconSize: [22, 22],
    iconAnchor: [11, 11],
    popupAnchor: [0, -10],
  });

export default function ImpactMap() {
  const { geo } = useGeo();
  const [orgs, setOrgs] = useState([]);
  const [housing, setHousing] = useState([]);
  const [ops, setOps] = useState([]);
  const [stories, setStories] = useState([]);
  const [enabled, setEnabled] = useState({ orgs: true, housing: true, opportunities: true, appeals: true, stories: true });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      http.get("/organizations").then((r) => r.data).catch(() => []),
      http.get("/housing-projects").then((r) => r.data).catch(() => []),
      http.get("/opportunities").then((r) => r.data).catch(() => []),
      http.get("/stories").then((r) => r.data).catch(() => []),
    ]).then(([o, h, op, st]) => {
      setOrgs(o);
      setHousing(h);
      setOps(op);
      setStories(st);
      setLoading(false);
    });
  }, []);

  // Attach lat/lng and colour to every item so we can render as pins.
  const pins = useMemo(() => {
    const all = [];
    if (enabled.orgs) {
      for (const o of orgs) {
        const ll = resolveLatLng(o.city, o.country);
        if (ll) all.push({ id: `org-${o.id}`, lat: ll[0], lng: ll[1], title: o.name, subtitle: o.category?.toUpperCase(), body: o.mission, city: `${o.city || ""}${o.city && o.country ? ", " : ""}${o.country || ""}`, href: `/organizations/${o.id}`, color: LAYERS[0].color, layer: "orgs" });
      }
    }
    if (enabled.housing) {
      for (const h of housing) {
        const ll = resolveLatLng(h.city, h.country);
        if (ll) all.push({ id: `hs-${h.id}`, lat: ll[0], lng: ll[1], title: h.title, subtitle: `${h.progress}% funded · $${h.raised?.toLocaleString?.() || h.raised}`, body: h.description, city: `${h.city || ""}${h.city && h.country ? ", " : ""}${h.country || ""}`, href: `/housing/${h.id}`, color: LAYERS[1].color, layer: "housing" });
      }
    }
    if (enabled.opportunities) {
      for (const p of ops) {
        // Opportunities inherit city/country from their organization if present.
        const org = orgs.find((o) => o.id === p.org_id);
        const ll = resolveLatLng(org?.city, org?.country);
        if (ll) all.push({ id: `op-${p.id}`, lat: ll[0], lng: ll[1], title: p.title, subtitle: p.category?.toUpperCase(), body: p.description, city: org ? `${org.city}, ${org.country}` : "", href: `/opportunities`, color: LAYERS[2].color, layer: "opportunities" });
      }
    }
    if (enabled.appeals) {
      for (const a of APPEALS) {
        const [c, ...rest] = (a.location || "").split(",").map((s) => s.trim());
        const country = rest.join(", ");
        const ll = resolveLatLng(c, country);
        if (ll) all.push({ id: `ap-${a.slug}`, lat: ll[0], lng: ll[1], title: a.title, subtitle: (a.urgency || "").toUpperCase(), body: a.subtitle, city: a.location, href: `/offer-help/${a.slug}`, color: LAYERS[3].color, layer: "appeals" });
      }
    }
    if (enabled.stories) {
      for (const s of stories) {
        // Prefer exact lat/lng saved on the story, else resolve city+country.
        let ll = null;
        if (typeof s.lat === "number" && typeof s.lng === "number") ll = [s.lat, s.lng];
        else ll = resolveLatLng(s.city, s.country);
        if (ll) all.push({ id: `st-${s.id}`, lat: ll[0], lng: ll[1], title: s.title, subtitle: (s.category || "STORY").toUpperCase(), body: s.body, city: [s.city, s.country].filter(Boolean).join(", "), href: `/stories`, color: LAYERS[4].color, layer: "stories" });
      }
    }
    return all;
  }, [enabled, orgs, housing, ops, stories]);

  const stats = useMemo(() => {
    const countries = new Set();
    pins.forEach((p) => {
      const country = p.city.split(",").pop()?.trim();
      if (country) countries.add(country);
    });
    return { pins: pins.length, countries: countries.size };
  }, [pins]);

  const initialCenter = useMemo(() => {
    if (geo?.country_code) {
      const [lat, lng] = resolveLatLng(geo.city, geo.country_name) || [20, 0];
      return [lat, lng];
    }
    return [20, 0];
  }, [geo]);

  return (
    <div data-testid="impact-map-page" className="max-w-[1400px] mx-auto px-6 lg:px-10 py-12 md:py-16">
      <div className="grid lg:grid-cols-12 gap-8 items-end">
        <div className="lg:col-span-8">
          <div className="overline mb-3">COMMUNITY IMPACT MAP</div>
          <h1 className="font-display text-4xl md:text-5xl leading-[1.05]">Every act of kindness, on the map.</h1>
          <p className="mt-4 max-w-2xl text-base leading-relaxed" style={{ color: "var(--wofy-text-soft)" }}>
            A live view of verified organizations, housing rebuilds, volunteer opportunities, and
            urgent appeals around the world. Tap a pin to see the story.
          </p>
        </div>
        <div className="lg:col-span-4 flex flex-wrap gap-4 justify-start lg:justify-end text-sm">
          <StatBadge label="Active pins" value={stats.pins} />
          <StatBadge label="Countries" value={stats.countries} />
        </div>
      </div>

      {/* Filter chips */}
      <div className="mt-10 flex flex-wrap gap-2">
        {LAYERS.map((l) => {
          const active = enabled[l.key];
          const Icon = l.icon;
          return (
            <button
              key={l.key}
              data-testid={`layer-${l.key}`}
              onClick={() => setEnabled((e) => ({ ...e, [l.key]: !e[l.key] }))}
              className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition-colors"
              style={{
                background: active ? l.color : "var(--wofy-surface)",
                color: active ? "#FDFBF7" : "var(--wofy-text-soft)",
                border: `1px solid ${active ? l.color : "var(--wofy-border)"}`,
              }}
            >
              <Icon className="w-4 h-4" />
              {l.label}
              <span className="ml-1 text-xs opacity-70">
                {pins.filter((p) => p.layer === l.key).length}
              </span>
            </button>
          );
        })}
      </div>

      {/* Map — the wrapper key defeats React 18 StrictMode double-init
          by giving each mount a unique DOM node. */}
      <MapWrapper
        center={initialCenter}
        zoom={geo?.country_code ? 4 : 2}
        pins={pins}
        geo={geo}
      />

      {/* Legend + attribution */}
      <div className="mt-6 flex flex-wrap items-center justify-between gap-4 text-xs" style={{ color: "var(--wofy-text-soft)" }}>
        <div className="flex flex-wrap items-center gap-4">
          {LAYERS.map((l) => (
            <div key={l.key} className="inline-flex items-center gap-2">
              <span className="w-3 h-3 rounded-full" style={{ background: l.color }} />
              {l.label}
            </div>
          ))}
          <div className="inline-flex items-center gap-2">
            <span className="w-3 h-3 rounded-full" style={{ background: "#5A3C89", opacity: 0.5, border: "2px solid #5A3C89" }} />
            You are here
          </div>
        </div>
        {loading && <div>Loading pins…</div>}
      </div>
    </div>
  );
}

const StatBadge = ({ label, value }) => (
  <div
    className="rounded-2xl px-5 py-3 border"
    style={{ background: "var(--wofy-surface)", borderColor: "var(--wofy-border)" }}
  >
    <div className="font-display text-2xl" style={{ color: "var(--wofy-primary)" }}>{value}</div>
    <div className="overline text-[10px] mt-1">{label}</div>
  </div>
);

// Isolated map wrapper written against **vanilla Leaflet** (not react-leaflet)
// to sidestep react-leaflet's "Map container is already initialized" error
// under React 18 StrictMode double-mount. We create the map instance
// imperatively inside a useEffect and clean it up on unmount, which is the
// canonical way Leaflet is meant to be used.
const MapWrapper = ({ center, zoom, pins, geo }) => {
  const containerRef = useRef(null);
  const mapRef = useRef(null);

  // Create the map exactly once per DOM node.
  useEffect(() => {
    if (!containerRef.current) return;
    // StrictMode may re-run this effect: if we already have a map bound to a
    // stale container (removed from DOM), just re-init on the fresh container.
    if (mapRef.current) {
      try { mapRef.current.remove(); } catch (e) { if (process.env.NODE_ENV === "development") console.warn("[ImpactMap] map.remove() during re-init:", e); }
      mapRef.current = null;
    }
    const map = L.map(containerRef.current, { scrollWheelZoom: true }).setView(center, zoom);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    }).addTo(map);
    mapRef.current = map;
    return () => {
      if (mapRef.current) {
        try { mapRef.current.remove(); } catch (e) { if (process.env.NODE_ENV === "development") console.warn("[ImpactMap] map.remove() on unmount:", e); }
        mapRef.current = null;
      }
    };
    // Intentional: create map only once. center/zoom updates are handled below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-pan when initial center/zoom change (e.g. after IP geo resolves).
  useEffect(() => {
    if (!mapRef.current) return;
    mapRef.current.setView(center, zoom);
  }, [center, zoom]);

  // Re-draw markers when pins or geo change.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const layerGroup = L.layerGroup().addTo(map);

    // "You are here" marker from IP/GPS geolocation
    if (geo) {
      const gll = resolveLatLng(geo.city, geo.country_name);
      if (gll) {
        L.circleMarker(gll, {
          radius: 10,
          color: "#5A3C89",
          fillColor: "#5A3C89",
          fillOpacity: 0.35,
          weight: 3,
        })
          .bindPopup(`<strong>You are here</strong><br/>${geo.city ? `${geo.city}, ` : ""}${geo.country_name || ""}`)
          .addTo(layerGroup);
      }
    }

    pins.forEach((p) => {
      const icon = buildIcon(p.color);
      const popupHtml = `
        <div style="min-width:220px">
          <div style="font-family:'JetBrains Mono',ui-monospace,monospace;font-size:10px;text-transform:uppercase;letter-spacing:.08em;color:${p.color}">
            ${p.subtitle || ""}
          </div>
          <div style="font-family:'Fraunces',serif;font-size:15px;line-height:1.2;margin-top:4px">${p.title}</div>
          ${p.city ? `<div style="margin-top:4px;font-size:11px;color:#726a5a">📍 ${p.city}</div>` : ""}
          ${p.body ? `<p style="margin-top:8px;font-size:11px;line-height:1.5">${p.body.slice(0, 220)}${p.body.length > 220 ? "…" : ""}</p>` : ""}
          <a href="${p.href}" style="display:inline-block;margin-top:10px;font-size:11px;color:${p.color};text-decoration:underline">Open story →</a>
        </div>`;
      L.marker([p.lat, p.lng], { icon }).bindPopup(popupHtml).addTo(layerGroup);
    });

    return () => {
      layerGroup.remove();
    };
  }, [pins, geo]);

  return (
    <div
      data-testid="impact-map"
      ref={containerRef}
      className="mt-8 rounded-3xl overflow-hidden border shadow-sm"
      style={{ borderColor: "var(--wofy-border)", height: "560px" }}
    />
  );
};
